import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image, Font } from '@react-pdf/renderer';
import { toInvoiceShape, DEFAULT_COMPANY } from '../config/CompanyDetails';

// Register fonts (optional - for better look)
// Font.register({ family: 'Helvetica', src: 'Helvetica' });

const styles = StyleSheet.create({
  page: {
    padding: 0,
    backgroundColor: '#FFFFFF',
    fontFamily: 'Helvetica',
  },
  // HEADER - Full width with logo
  header: {
    width: '100%',
    backgroundColor: '#0A1929',
    padding: 20,
    paddingBottom: 15,
    marginBottom: 20,
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerLeft: {
    flexDirection: 'column',
  },
  headerLogo: {
    width: 80,
    height: 80,
    objectFit: 'contain',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  headerSub: {
    fontSize: 10,
    color: '#CCCCCC',
    marginTop: 2,
  },
  headerDetails: {
    textAlign: 'right',
  },
  headerDetailText: {
    fontSize: 9,
    color: '#FFFFFF',
    marginBottom: 2,
  },
  // INVOICE TITLE
  invoiceTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#0A1929',
    textAlign: 'center',
    marginBottom: 15,
    letterSpacing: 2,
  },
  // CONTENT CONTAINER
  content: {
    paddingHorizontal: 30,
    paddingBottom: 30,
  },
  // INFO ROW (Invoice #, Date, Status)
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
    padding: 10,
    backgroundColor: '#F5F7FA',
    borderRadius: 4,
  },
  infoItem: {
    flexDirection: 'column',
  },
  infoLabel: {
    fontSize: 8,
    color: '#666666',
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  infoValue: {
    fontSize: 11,
    color: '#0A1929',
    fontWeight: 'bold',
    marginTop: 2,
  },
  infoValuePaid: {
    fontSize: 11,
    color: '#00A86B',
    fontWeight: 'bold',
    marginTop: 2,
  },
  // DIVIDER
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: '#E8ECEF',
    marginVertical: 15,
  },
  // TWO COLUMN SECTION
  twoCol: {
    flexDirection: 'row',
    gap: 20,
    marginBottom: 20,
  },
  col: {
    flex: 1,
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#0A1929',
    marginBottom: 5,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  sectionText: {
    fontSize: 9,
    color: '#333333',
    marginBottom: 2,
  },
  // TABLE
  table: {
    marginVertical: 15,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#0A1929',
    padding: 8,
    borderRadius: 4,
  },
  tableHeaderText: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#FFFFFF',
    textTransform: 'uppercase',
  },
  tableRow: {
    flexDirection: 'row',
    padding: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#EEEEEE',
  },
  tableRowEven: {
    flexDirection: 'row',
    padding: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#EEEEEE',
    backgroundColor: '#F9FAFB',
  },
  colDescription: { flex: 3 },
  colQty: { flex: 1, textAlign: 'center' },
  colRate: { flex: 1, textAlign: 'right' },
  colAmount: { flex: 1.5, textAlign: 'right' },
  tableText: {
    fontSize: 9,
    color: '#333333',
  },
  // TOTALS
  totalsBox: {
    marginTop: 10,
    padding: 15,
    backgroundColor: '#F5F7FA',
    borderRadius: 4,
    alignItems: 'flex-end',
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: 3,
  },
  totalLabel: {
    fontSize: 9,
    color: '#666666',
    width: 100,
    textAlign: 'right',
  },
  totalValue: {
    fontSize: 9,
    color: '#0A1929',
    width: 100,
    textAlign: 'right',
    fontWeight: 'medium',
  },
  totalRowFinal: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 5,
    paddingTop: 5,
    borderTopWidth: 2,
    borderTopColor: '#0A1929',
  },
  totalLabelFinal: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#0A1929',
    width: 100,
    textAlign: 'right',
  },
  totalValueFinal: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#00A86B',
    width: 100,
    textAlign: 'right',
  },
  // PAYMENT DETAILS
  paymentBox: {
    marginTop: 15,
    padding: 12,
    backgroundColor: '#E8F5E9',
    borderRadius: 4,
  },
  paymentText: {
    fontSize: 9,
    color: '#2E7D32',
  },
  // FOOTER
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 30,
    right: 30,
    textAlign: 'center',
    borderTopWidth: 1,
    borderTopColor: '#E8ECEF',
    paddingTop: 10,
  },
  footerText: {
    fontSize: 7,
    color: '#999999',
    textAlign: 'center',
  },
});

// Helper to format currency
const formatCurrency = (amount) => {
  return `R${(amount || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
};

// Helper to format date
const formatDate = (date) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleDateString('en-ZA');
};

// Main PDF Component
const InvoicePDF = ({ invoice, job, subbie, companyDetails }) => {
  // companyDetails uses the shared invoice shape; default = single source.
  const cd = companyDetails || toInvoiceShape(DEFAULT_COMPANY);
  // Calculate values
  const subtotal = invoice.subtotal || invoice.base_amount || 0;
  const vatAmount = invoice.vat_amount || 0;
  const total = invoice.total_amount || subtotal + vatAmount;
  const isPaid = invoice.status === 'paid';
  
  // Build line items
  const lineItems = [];
  
  // Main haulage item.
  // A weight of exactly 1 is how a PER-LOAD charge is stored (rate = the whole amount), so "1 MT"
  // beside a R44,000 line would be a plain falsehood. MT only when the weight is a real weight.
  if (invoice.weight_mt && invoice.rate_per_mt) {
    lineItems.push({
      description: `Haulage Service - ${job?.commodity || 'N/A'}`,
      qty: Number(invoice.weight_mt) > 1 ? `${invoice.weight_mt} MT` : String(invoice.weight_mt),
      rate: formatCurrency(invoice.rate_per_mt),
      amount: formatCurrency(invoice.base_amount || invoice.weight_mt * invoice.rate_per_mt),
    });
  }
  
  // Extras
  if (invoice.extras && invoice.extras.length > 0) {
    invoice.extras.forEach((extra) => {
      if (extra.description && extra.amount) {
        lineItems.push({
          description: extra.description,
          qty: '-',
          rate: '-',
          amount: formatCurrency(extra.amount),
        });
      }
    });
  }

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* HEADER - Full width with dark background */}
        <View style={styles.header}>
          <View style={styles.headerContent}>
            <View style={styles.headerLeft}>
              {cd.logo && (
                <Image src={cd.logo} style={styles.headerLogo} />
              )}
              <Text style={styles.headerTitle}>ONE PURPOSE LOGISTICS</Text>
              <Text style={styles.headerSub}>PTY LTD</Text>
            </View>
            <View style={styles.headerDetails}>
              <Text style={styles.headerDetailText}>{cd.address}</Text>
              <Text style={styles.headerDetailText}>Tel: {cd.phone}</Text>
              <Text style={styles.headerDetailText}>Email: {cd.email}</Text>
              <Text style={styles.headerDetailText}>Reg: {cd.regNumber}</Text>
              {cd.vatNumber && (
                <Text style={styles.headerDetailText}>VAT: {cd.vatNumber}</Text>
              )}
            </View>
          </View>
        </View>

        {/* CONTENT */}
        <View style={styles.content}>
          {/* Invoice Title */}
          <Text style={styles.invoiceTitle}>INVOICE</Text>

          {/* Info Row */}
          <View style={styles.infoRow}>
            <View style={styles.infoItem}>
              <Text style={styles.infoLabel}>Invoice Number</Text>
              <Text style={styles.infoValue}>{invoice.invoice_number}</Text>
            </View>
            <View style={styles.infoItem}>
              <Text style={styles.infoLabel}>Job Number</Text>
              <Text style={styles.infoValue}>{job?.job_number || 'N/A'}</Text>
            </View>
            <View style={styles.infoItem}>
              <Text style={styles.infoLabel}>Date</Text>
              <Text style={styles.infoValue}>{formatDate(invoice.created_at)}</Text>
            </View>
            <View style={styles.infoItem}>
              <Text style={styles.infoLabel}>Status</Text>
              <Text style={isPaid ? styles.infoValuePaid : styles.infoValue}>
                {isPaid ? '✅ PAID' : '⏳ PENDING'}
              </Text>
            </View>
          </View>

          {/* Divider */}
          <View style={styles.divider} />

          {/* Two Column: Subbie & Job */}
          <View style={styles.twoCol}>
            <View style={styles.col}>
              <Text style={styles.sectionTitle}>Subcontractor</Text>
              <Text style={styles.sectionText}>{subbie?.company_name || 'N/A'}</Text>
              <Text style={styles.sectionText}>Contact: {subbie?.contact_person || 'N/A'}</Text>
              <Text style={styles.sectionText}>Phone: {subbie?.phone || 'N/A'}</Text>
              <Text style={styles.sectionText}>Email: {subbie?.email || 'N/A'}</Text>
            </View>
            <View style={styles.col}>
              <Text style={styles.sectionTitle}>Job Details</Text>
              <Text style={styles.sectionText}>Job #: {job?.job_number || 'N/A'}</Text>
              <Text style={styles.sectionText}>Commodity: {job?.commodity || 'N/A'}</Text>
              <Text style={styles.sectionText}>Origin: {job?.origin || 'N/A'}</Text>
              <Text style={styles.sectionText}>Destination: {job?.destination || 'N/A'}</Text>
            </View>
          </View>

          {/* Divider */}
          <View style={styles.divider} />

          {/* Table */}
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableHeaderText, styles.colDescription]}>Description</Text>
              <Text style={[styles.tableHeaderText, styles.colQty]}>Qty</Text>
              <Text style={[styles.tableHeaderText, styles.colRate]}>Rate</Text>
              <Text style={[styles.tableHeaderText, styles.colAmount]}>Amount</Text>
            </View>
            
            {lineItems.map((item, index) => (
              <View key={index} style={index % 2 === 0 ? styles.tableRow : styles.tableRowEven}>
                <Text style={[styles.tableText, styles.colDescription]}>{item.description}</Text>
                <Text style={[styles.tableText, styles.colQty]}>{item.qty}</Text>
                <Text style={[styles.tableText, styles.colRate]}>{item.rate}</Text>
                <Text style={[styles.tableText, styles.colAmount]}>{item.amount}</Text>
              </View>
            ))}
          </View>

          {/* Totals */}
          <View style={styles.totalsBox}>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Subtotal</Text>
              <Text style={styles.totalValue}>{formatCurrency(subtotal)}</Text>
            </View>
            {invoice.vat_applicable && (
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>VAT (15%)</Text>
                <Text style={styles.totalValue}>{formatCurrency(vatAmount)}</Text>
              </View>
            )}
            <View style={styles.totalRowFinal}>
              <Text style={styles.totalLabelFinal}>TOTAL</Text>
              <Text style={styles.totalValueFinal}>{formatCurrency(total)}</Text>
            </View>
          </View>

          {/* Payment Details (if paid) */}
          {isPaid && (
            <View style={styles.paymentBox}>
              <Text style={styles.paymentText}>
                ✅ Payment Confirmed - {formatDate(invoice.payment_date)}
              </Text>
              <Text style={styles.paymentText}>
                Method: {invoice.payment_method || 'Bank Transfer'}
              </Text>
              {invoice.pop_url && (
                <Text style={styles.paymentText}>
                  POP Reference: {invoice.pop_url.split('/').pop() || 'N/A'}
                </Text>
              )}
            </View>
          )}

          {/* Thank You */}
          <View style={{ marginTop: 20, alignItems: 'center' }}>
            <Text style={{ fontSize: 10, color: '#666666' }}>
              Thank you for your business!
            </Text>
          </View>
        </View>

        {/* FOOTER */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            {cd.name} │ {cd.address} │ Tel: {cd.phone} │ Email: {cd.email}
          </Text>
          <Text style={[styles.footerText, { marginTop: 3 }]}>
            Reg: {cd.regNumber} │ Terms: {invoice.payment_terms || '30 Days'}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default InvoicePDF;
